import articleConfig from '~/config/article';
import { ENV } from '~/config/article/env';

/** Each config category should have following keys */
interface IConfigValues {
  comments: string;
  emotes: string;
  newsfeed: string;
  trafficExchange: string;
  modal: string;
  powerbar: string;
  verticalPowerbar: string;
  sharebar: string;
  verticalSharebar: string;
  subscribe: string;
  audio: string;
  quiz: string;
  quizAlt: string;
}

interface IIframelessWidgetConfigValues {
  comments: string;
}

class WidgetsConfig {
  // Base url where widgets are deployed
  private static baseUrl: string = ENV.WIDGETS_BASE_URL || 'https://cdn.vuukle.com/widgets';

  public static iframelessWidgetUrls: IIframelessWidgetConfigValues = {
    comments: `${WidgetsConfig.baseUrl}/comments.modern.js`,
  };

  public static legacyIframelessWidgetUrls: IIframelessWidgetConfigValues = {
    comments: `${WidgetsConfig.baseUrl}/comments.legacy.js`,
  };

  public static urls: IConfigValues = {
    comments: `${WidgetsConfig.baseUrl}/index.html`,
    emotes: `${WidgetsConfig.baseUrl}/emotes.html`,
    modal: `${WidgetsConfig.baseUrl}/modal.html`,
    newsfeed: `${WidgetsConfig.baseUrl}/newsfeed.js`,
    trafficExchange: `${WidgetsConfig.baseUrl}/trafficexchange.js`,
    powerbar: `${WidgetsConfig.baseUrl}/powerbar.html`,
    sharebar: `${WidgetsConfig.baseUrl}/sharebar.html`,
    subscribe: `${WidgetsConfig.baseUrl}/subscribe.html`,
    verticalSharebar: `${WidgetsConfig.baseUrl}/sharebar.html`,
    verticalPowerbar: `${WidgetsConfig.baseUrl}/powerbar.html`,
    audio: `${WidgetsConfig.baseUrl}/audio.html`,
    quiz: `${WidgetsConfig.baseUrl}/quiz.html`,
    quizAlt: `${WidgetsConfig.baseUrl}/quiz.html`,
  };

  public static legacyUrls: IConfigValues = {
    comments: `${WidgetsConfig.baseUrl}/index-legacy.html`,
    emotes: `${WidgetsConfig.baseUrl}/emotes-legacy.html`,
    modal: `${WidgetsConfig.baseUrl}/modal-legacy.html`,
    sharebar: `${WidgetsConfig.baseUrl}/sharebar-legacy.html`,
    verticalSharebar: `${WidgetsConfig.baseUrl}/sharebar-legacy.html`,
    powerbar: `${WidgetsConfig.baseUrl}/powerbar-legacy.html`,
    verticalPowerbar: `${WidgetsConfig.baseUrl}/powerbar-legacy.html`,
    newsfeed: `${WidgetsConfig.baseUrl}/newsfeed.js`,
    trafficExchange: `${WidgetsConfig.baseUrl}/trafficexchange.js`,
    subscribe: `${WidgetsConfig.baseUrl}/subscribe-legacy.html`,
    audio: `${WidgetsConfig.baseUrl}/audio-legacy.html`,
    quiz: `${WidgetsConfig.baseUrl}/quiz.html`,
    quizAlt: `${WidgetsConfig.baseUrl}/quiz.html`,
  };

  /**
   * Main widgets selectors
   */
  public static selectors: IConfigValues = {
    comments: '#vuukle-comments',
    emotes: '#vuukle-emote',
    modal: 'body',
    newsfeed: '#vuukle-newsfeed',
    trafficExchange: '#vuukle-traffic-exchange',
    quiz: '#vuukle-quiz',
    quizAlt: '#quiz',
    sharebar: '.vuukle-sharebar',
    subscribe: '.vuukle-subscribe',
    verticalSharebar: '.vuukle-sharebar-vertical',
    powerbar: '.vuukle-powerbar',
    verticalPowerbar: '.vuukle-powerbar-vertical',
    audio: '.vuukle-audio',
  };

  public static versions = {
    comments: '3.42.0',
    emotes: '3.13.6',
    modal: '3.20.1',
    sharebar: '2.22.3',
    powerbar: '2.22.3',
    subscribe: '1.1.9',
    quiz: '1.32.7',
    audio: '1.2.5',
  };
}

/**
 * Since we need ability to upload build by branches for our widgets, there should be a way for platform to
 * configure custom widgets links to use these builds. It's available through the articleConfig.dev property
 * {@see articleConfig}
 *
 * Our QA can configure custom url for widgets builds and then use it as it's regular thing
 */
if (ENV.NODE_ENV === 'development' || ENV.IS_STAGING) {
  WidgetsConfig.iframelessWidgetUrls.comments =
    articleConfig.dev?.commentsUrl || WidgetsConfig.iframelessWidgetUrls.comments;
  WidgetsConfig.urls.comments = articleConfig.dev?.commentsUrl || WidgetsConfig.urls.comments;
  WidgetsConfig.urls.emotes = articleConfig.dev?.emotesUrl || WidgetsConfig.urls.emotes;
  WidgetsConfig.urls.modal = articleConfig.dev?.modalUrl || WidgetsConfig.urls.modal;
  WidgetsConfig.urls.newsfeed = articleConfig.dev?.newsfeedUrl || WidgetsConfig.urls.newsfeed;
  WidgetsConfig.urls.trafficExchange = articleConfig.dev?.trafficexchangeUrl || WidgetsConfig.urls.trafficExchange;
  WidgetsConfig.urls.powerbar = articleConfig.dev?.powerbarUrl || WidgetsConfig.urls.powerbar;
  WidgetsConfig.urls.verticalPowerbar = WidgetsConfig.urls.powerbar;
  WidgetsConfig.urls.sharebar =
    articleConfig.dev?.sharebarUrl || WidgetsConfig.urls.sharebar || WidgetsConfig.urls.powerbar;
  WidgetsConfig.urls.verticalSharebar = WidgetsConfig.urls.sharebar || WidgetsConfig.urls.powerbar;
  WidgetsConfig.urls.subscribe = articleConfig.dev?.subscribeUrl || WidgetsConfig.urls.subscribe;
  WidgetsConfig.urls.audio = articleConfig.dev?.audioUrl || WidgetsConfig.urls.audio;
  WidgetsConfig.urls.quiz = articleConfig.dev?.quizUrl || WidgetsConfig.urls.quiz || WidgetsConfig.urls.quizAlt;
}

/** Check if the browser supports string matchAll function which is available in the modern browsers that support ES2020 */
const supportsES2020 = () => {
  return typeof ''.matchAll !== 'undefined';
};

// Use legacy version of the widgets if the user client doesn't support ES2020
if (!supportsES2020() && !articleConfig.hasOwnProperty('dev')) {
  WidgetsConfig.iframelessWidgetUrls.comments = WidgetsConfig.legacyIframelessWidgetUrls.comments;
  WidgetsConfig.urls.comments = WidgetsConfig.legacyUrls.comments;
  WidgetsConfig.urls.emotes = WidgetsConfig.legacyUrls.emotes;
  WidgetsConfig.urls.modal = WidgetsConfig.legacyUrls.modal;
  WidgetsConfig.urls.powerbar = WidgetsConfig.legacyUrls.powerbar;
  WidgetsConfig.urls.verticalPowerbar = WidgetsConfig.legacyUrls.verticalPowerbar;
  WidgetsConfig.urls.sharebar = WidgetsConfig.legacyUrls.sharebar;
  WidgetsConfig.urls.verticalSharebar = WidgetsConfig.legacyUrls.verticalSharebar;
  WidgetsConfig.urls.subscribe = WidgetsConfig.legacyUrls.subscribe;
  WidgetsConfig.urls.audio = WidgetsConfig.legacyUrls.audio;
  WidgetsConfig.urls.quiz = WidgetsConfig.legacyUrls.quiz || WidgetsConfig.legacyUrls.quizAlt;
}

export default WidgetsConfig;
